import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, RefreshCw, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import confetti from 'canvas-confetti';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import remarkLatexSanitizer from '@/utils/remarkLatexSanitizer';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { sanitizeChatMarkdown } from '@/utils/chatMarkdownSanitizer';
import { visit } from 'unist-util-visit';

const rehypeBlanks = () => {
    return (tree) => {
        visit(tree, 'text', (node, index, parent) => {
            if (!node.value || typeof node.value !== 'string') return;
            if (!/\[\[BLANK_\d+\]\]/.test(node.value)) return;

            // CRITICAL FIX: Do not inject React dropdown components inside math nodes.
            // React DOM will crash if it tries to render an HTML <select> inside a MathML <annotation> or similar SVG namespace.
            if (parent && parent.properties && parent.properties.className && Array.isArray(parent.properties.className)) {
                if (parent.properties.className.includes('math') || parent.properties.className.includes('katex')) {
                    return;
                }
            }

            const parts = node.value.split(/(\[\[BLANK_\d+\]\])/);
            const newChildren = parts.map(part => {
                const match = part.match(/\[\[BLANK_(\d+)\]\]/);
                if (match) {
                    return {
                        type: 'element',
                        tagName: 'span',
                        properties: { className: ['interactive-blank'], 'data-index': match[1] },
                        children: []
                    };
                }
                return { type: 'text', value: part };
            }).filter(n => n.type !== 'text' || n.value !== '');

            if (parent && parent.children) {
                parent.children.splice(index, 1, ...newChildren);
                return index + newChildren.length;
            }
        });
    };
};

const MarkdownText = ({ text }) => {
    if (!text) return null;
    return (
        <ReactMarkdown
            remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
            rehypePlugins={[rehypeBlanks, [rehypeKatex, { throwOnError: false, output: 'html', strict: false, trust: true }]]}
            components={{ p: React.Fragment }}
        >
            {sanitizeChatMarkdown(text)}
        </ReactMarkdown>
    );
};

/**
 * Interactive Quiz Widget
 * Supports: 'match_following', 'fill_blanks'
 */
const InteractiveQuiz = ({ data }) => {
    const [isCompleted, setIsCompleted] = useState(false);
    const [score, setScore] = useState(0);

    if (!data || !data.type) return null;

    const confettiRunRef = useRef(false);

    const handleComplete = useCallback((finalScore, total) => {
        setScore(finalScore);
        setIsCompleted(true);
        if (finalScore === total && !confettiRunRef.current) {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 }
            });
            confettiRunRef.current = true;
        }
    }, []);

    return (
        <div className="my-6 w-full max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b flex justify-between items-center">
                    <div>
                        <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-100 px-2 py-1 rounded-full">
                            {data.type === 'match' ? 'Match the Following' : 'Fill in the Blanks'}
                        </span>
                        {/* Hide question for fill_blanks if it contains %BLANK% or [BLANK] as it is redundant */}
                        {data.question && !(data.type === 'fill_blanks' && (
                            data.question.includes('%BLANK%') ||
                            data.question.includes('___') ||
                            data.question.includes('[BLANK]') ||
                            data.question.includes('\\BLANK') ||
                            data.question.trim() === data.text?.trim()
                        )) && (
                                <h3 className="font-semibold text-gray-800 mt-1"><MarkdownText text={data.question} /></h3>
                            )}
                    </div>
                    {isCompleted && (
                        <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold animate-in fade-in zoom-in">
                            <Trophy className="w-4 h-4" />
                            {score}/{data.items?.length || data.blanks?.length}
                        </div>
                    )}
                </div>

                {/* Game Body */}
                <div className="p-6">
                    {data.type === 'match' && (
                        <MatchGame items={data.items} onComplete={handleComplete} />
                    )}
                    {data.type === 'fill_blanks' && (
                        <FillBlanksGame
                            text={data.text}
                            blanks={data.blanks}
                            options={data.options}
                            onComplete={handleComplete}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Sub-Component: Match the Following (Modern Magnetic Design) ---
const MatchGame = ({ items = [], onComplete }) => {
    const [leftSelected, setLeftSelected] = useState(null);
    const [matches, setMatches] = useState({}); // { leftId: rightId }
    const [status, setStatus] = useState({}); // { leftId: 'correct' | 'wrong' }
    const [shuffledRight, setShuffledRight] = useState([]);

    // Initialize right side
    useEffect(() => {
        const rightItems = items.map(i => ({ id: i.id, text: i.right }));
        // Shuffle deterministically or randomly - let's do random
        setShuffledRight([...rightItems].sort(() => Math.random() - 0.5));
    }, [JSON.stringify(items)]);

    const handleCardClick = (side, id) => {
        if (side === 'left') {
            if (status[id] === 'correct') return;
            // If already selected, deselect
            if (leftSelected === id) {
                setLeftSelected(null);
                return;
            }
            setLeftSelected(id);
        } else {
            // Side is right
            if (!leftSelected) return; // Must select left first (or implement bidirectional later)

            // Logic match
            handleMatchAttempt(leftSelected, id);
        }
    };

    const handleMatchAttempt = (leftId, rightId) => {
        // Check correctness: In this data structure, item.id links left and right parts.
        // So correct match is when leftId === rightId (assuming rightId came from the item with that ID)

        const isCorrect = leftId === rightId; // Since we mapped right items maintaining original ID

        const newMatches = { ...matches, [leftId]: rightId };
        setMatches(newMatches);

        if (isCorrect) {
            setStatus(prev => ({ ...prev, [leftId]: 'correct' }));
            setLeftSelected(null);
        } else {
            setStatus(prev => ({ ...prev, [leftId]: 'wrong' }));
            // Reset after delay
            setTimeout(() => {
                setStatus(prev => {
                    const next = { ...prev };
                    delete next[leftId];
                    return next;
                });
                setMatches(prev => {
                    const next = { ...prev };
                    delete next[leftId];
                    return next;
                });
                setLeftSelected(null);
            }, 800);
        }
    };

    // Check completion
    useEffect(() => {
        const correctCount = Object.values(status).filter(s => s === 'correct').length;
        if (correctCount === items.length && items.length > 0) {
            onComplete(items.length, items.length);
        }
    }, [status, items.length, onComplete]);

    return (
        <div className="grid grid-cols-2 gap-8 relative">
            {/* Center "Magnetic Field" Decor */}
            <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-blue-200 to-transparent -translate-x-1/2" />

            {/* Left Column */}
            <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 text-center">Items</h4>
                <AnimatePresence>
                    {items.map((item) => (
                        <MagneticCard
                            key={`left-${item.id}`}
                            text={item.left}
                            isSelected={leftSelected === item.id}
                            isMatched={status[item.id] === 'correct'}
                            isWrong={status[item.id] === 'wrong'}
                            onClick={() => handleCardClick('left', item.id)}
                            side="left"
                        />
                    ))}
                </AnimatePresence>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 text-center">Definitions</h4>
                <AnimatePresence>
                    {shuffledRight.map((item) => {
                        // Check if this right card is involved in a match
                        const involvedLeftId = Object.keys(matches).find(key => matches[key] === item.id);
                        const matchStatus = involvedLeftId ? status[involvedLeftId] : null;

                        return (
                            <MagneticCard
                                key={`right-${item.id}`}
                                text={item.text}
                                isSelected={false} // Right side doesn't stay "selected" in this interaction model
                                isMatched={matchStatus === 'correct'}
                                isWrong={matchStatus === 'wrong'}
                                onClick={() => handleCardClick('right', item.id)}
                                side="right"
                                isTarget={!!leftSelected} // Highlight as potential target
                            />
                        );
                    })}
                </AnimatePresence>
            </div>
        </div>
    );
};

// Reusable Magnetic Card
const MagneticCard = ({ text, isSelected, isMatched, isWrong, onClick, side, isTarget }) => {
    return (
        <motion.button
            layout
            onClick={onClick}
            initial={{ opacity: 0, y: 10 }}
            animate={{
                opacity: 1,
                y: 0,
                scale: isSelected ? 1.02 : 1,
                borderColor: isMatched ? 'hsl(var(--green-bright))' : isWrong ? 'hsl(var(--destructive))' : isSelected ? 'hsl(var(--primary))' : 'rgba(0,0,0,0)'
            }}
            whileHover={{ scale: isMatched ? 1 : 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={cn(
                "w-full p-4 rounded-xl text-sm font-medium text-left bg-[hsl(var(--card-bg))] text-gray-900 dark:text-gray-100 relative overflow-hidden transition-all duration-300",
                "border shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_16px_rgba(0,0,0,0.08)]",
                // Selected: keep the card readable, add a glow (no light washout)
                isSelected && "ring-2 ring-cyan-400 ring-offset-2 ring-offset-background shadow-[0_0_0_4px_rgba(34,211,238,0.22)]",
                // Correct: subtle success tint + strong border; keep text readable
                isMatched && "border-emerald-500 bg-emerald-500/10 text-emerald-950",
                // Wrong: subtle error tint + strong border; keep text readable
                isWrong && "border-red-500 bg-red-500/10 text-red-950 animate-shake",
                // Target candidates (right side) when a left item is selected
                isTarget && side === 'right' && !isMatched && !isWrong && "border-cyan-300/60 border-dashed bg-cyan-500/10 hover:bg-cyan-500/15"
            )}
        >
            {/* Glass Shim */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent pointer-events-none" />

            <div className="flex items-center justify-between relative z-10">
                <span
                    className={cn(
                        isMatched && "text-emerald-900",
                        isWrong && "text-red-900",
                        !isMatched && !isWrong && isTarget && side === 'right' && "text-cyan-900 font-medium",
                        !isMatched && !isWrong && (!isTarget || side !== 'right') && "text-gray-900 dark:text-gray-100"
                    )}
                >
                    <MarkdownText text={text} />
                </span>
                {isMatched && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                        <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-300" />
                    </motion.div>
                )}
                {isWrong && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                        <X className="w-4 h-4 text-red-600 dark:text-red-300" />
                    </motion.div>
                )}
                {side === 'left' && isSelected && !isMatched && !isWrong && (
                    <motion.div
                        layoutId="connector-dot"
                        className="w-2 h-2 rounded-full bg-blue-500 absolute -right-6"
                    />
                )}
            </div>
        </motion.button>
    );
};

// --- Sub-Component: Fill in the Blanks ---
const FillBlanksGame = ({ text, blanks = [], options = [], onComplete }) => {
    // blanks: array of answers in order
    // options: array of all possible words (including distractors)
    const [answers, setAnswers] = useState({}); // { index: 'word' }
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [results, setResults] = useState({}); // { index: true/false }

    const handleSelect = (index, word) => {
        if (isSubmitted) return;
        setAnswers(prev => ({ ...prev, [index]: word }));
    };

    const formatOption = (opt) => {
        if (typeof opt !== 'string') return opt;
        if (opt.includes('$') || opt.includes('\\(') || opt.includes('\\[')) return opt;

        // If it contains a backslash command (like \frac), a caret, an underscore, 
        // or math operators
        const hasMathOperators = /[\\][a-zA-Z]+|\^|_/.test(opt);
        const hasMathSymbols = /[+\-=><]\s*\w/.test(opt) || /\d\w/.test(opt);

        if (hasMathOperators || hasMathSymbols || opt.includes('frac')) {
            return `$${opt}$`;
        }

        return opt;
    };

    const checkAnswers = () => {
        let score = 0;
        const newResults = {};

        blanks.forEach((correctAnswer, idx) => {
            const isCorrect = answers[idx] === correctAnswer;
            newResults[idx] = isCorrect;
            if (isCorrect) score++;
        });

        setResults(newResults);
        setIsSubmitted(true);
        onComplete(score, blanks.length);
    };

    const getNormalizedText = () => {
        let count = 0;
        let normalized = String(text || '');

        // 1. Eject any blank token that is INSIDE KaTeX math blocks 
        // This splits the math block so the React dropdown anchors securely outside the SVG namespace.
        const mathBlankRegex = /\$([^$]*?)(?:\\[a-zA-Z]*BLANK|%BLANK%|\[BLANK\]|\\text\{BLANK\}|\\text\{___\}|_{3,})([^$]*?)\$/gi;
        normalized = normalized.replace(mathBlankRegex, (match, before, after) => {
            let res = '';
            if (before.trim()) res += '$' + before.trim() + '$ ';
            res += '[[BLANK_PLACEHOLDER]]';
            if (after.trim()) res += ' $' + after.trim() + '$';
            return res;
        });

        // 2. Normalize all blanks (including the ejected ones, and any that were already outside math)
        normalized = normalized
            .replace(/%BLANK\d*%/gi, () => `[[BLANK_${count++}]]`)
            .replace(/\[BLANK\]/gi, () => `[[BLANK_${count++}]]`)
            .replace(/\\[a-zA-Z]*BLANK/gi, () => `[[BLANK_${count++}]]`) // Catch \BLANK
            .replace(/\\text\{BLANK\}/gi, () => `[[BLANK_${count++}]]`)
            .replace(/_{3,}/g, () => `[[BLANK_${count++}]]`)
            .replace(/\[\[BLANK_PLACEHOLDER\]\]/g, () => `[[BLANK_${count++}]]`);

        // 3. Failsafe: if the LLM completely forgot to insert the blank into `text`, but blanks exist, append them to the end!
        if (count < blanks.length) {
            for (let i = count; i < blanks.length; i++) {
                normalized += ` [[BLANK_${count++}]]`;
            }
        }

        return normalized;
    };

    const normalizedText = getNormalizedText();


    return (
        <div className="space-y-6">
            <div className="text-lg leading-loose text-gray-800 font-medium whitespace-pre-wrap">
                <ReactMarkdown
                    remarkPlugins={[remarkLatexSanitizer, remarkMath]}
                    rehypePlugins={[rehypeBlanks, [rehypeKatex, { strict: false, throwOnError: false }]]}
                    components={{
                        p: React.Fragment,
                        // Override our custom injected span tags to render interactive dropdowns
                        span: (props) => {
                            if (props.className === 'interactive-blank') {
                                const blankIndex = parseInt(props['data-index'], 10);
                                if (blankIndex >= blanks.length) return <span {...props} />; // Fallback

                                return (
                                    <span key={`blank-${blankIndex}`} className="inline-flex items-center mx-1 align-baseline relative top-1">
                                        <Select
                                            value={answers[blankIndex] || ''}
                                            onValueChange={(val) => handleSelect(blankIndex, val)}
                                            disabled={isSubmitted}
                                        >
                                            <SelectTrigger
                                                className={cn(
                                                    "inline-flex h-auto w-auto min-w-[120px] border-b-2 border-x-0 border-t-0 border-gray-300 rounded-none bg-transparent px-2 py-0.5 outline-none focus:border-blue-500 focus:ring-0 transition-colors text-base font-sans shadow-none whitespace-nowrap",
                                                    isSubmitted && results[blankIndex] ? "border-green-500 text-green-700 font-bold" :
                                                        isSubmitted && !results[blankIndex] ? "border-red-500 text-red-700" : ""
                                                )}
                                            >
                                                <SelectValue placeholder="Select..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {options.map(opt => (
                                                    <SelectItem key={opt} value={opt}>
                                                        <MarkdownText text={formatOption(opt)} />
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {isSubmitted && !results[blankIndex] && (
                                            <span className="text-xs text-green-600 ml-1 font-sans">({blanks[blankIndex]})</span>
                                        )}
                                    </span>
                                );
                            }
                            // Default behavior for all other spans (including KaTeX ones)
                            return <span {...props} />;
                        }
                    }}
                >
                    {sanitizeChatMarkdown(normalizedText)}
                </ReactMarkdown>
            </div>

            {!isSubmitted && (
                <div className="flex justify-end pt-4">
                    <Button onClick={checkAnswers} disabled={Object.keys(answers).length < blanks.length}>
                        Check Answers
                    </Button>
                </div>
            )}
        </div>
    );
};

export default InteractiveQuiz;
